import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { IconMic } from '../icons';
import { Spinner } from '../components';

/**
 * Dictating a note: press, speak, press again, and the words are added to the
 * text box. Two ways, chosen per clinic in its settings:
 *
 *   device   the speech model runs on this computer (whisper.worker.ts). The
 *            voice never leaves it; works in the Windows app and offline.
 *   browser  the browser's own speech service (Chrome/Edge). Instant and very
 *            accurate, but the audio goes to Google or Microsoft, which the
 *            clinic accepts by choosing it. Not in the Windows app, where the
 *            device way is used instead.
 *
 * The phone apps will plug their own system recognisers in behind the same
 * button (onText), so a note is dictated the same way everywhere.
 */
export type DictationMode = 'device' | 'browser' | 'off';

let clinicMode: DictationMode = 'device';
/** Set by the app from the active clinic's settings. */
export function setDictationMode(mode: DictationMode | undefined) {
  clinicMode = mode ?? 'device';
}

type SpeechRec = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};
const SpeechRecognitionCtor = (): (new () => SpeechRec) | null =>
  ((window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec }).SpeechRecognition ??
    (window as unknown as { webkitSpeechRecognition?: new () => SpeechRec }).webkitSpeechRecognition ??
    null);

let worker: Worker | null = null;
let nextId = 1;
function speechWorker(): Worker {
  worker ??= new Worker(new URL('./whisper.worker.ts', import.meta.url), { type: 'module' });
  return worker;
}

/** A recording as the model wants it: mono, 16 kHz, floats. */
async function toPcm16k(blob: Blob): Promise<Float32Array> {
  const ctx = new AudioContext({ sampleRate: 16_000 });
  try {
    const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
    if (buf.numberOfChannels === 1) return buf.getChannelData(0);
    const a = buf.getChannelData(0);
    const b = buf.getChannelData(1);
    const out = new Float32Array(a.length);
    for (let i = 0; i < a.length; i++) out[i] = (a[i]! + b[i]!) / 2;
    return out;
  } finally {
    void ctx.close();
  }
}

export function Dictate({ onText, disabled }: { onText: (text: string) => void; disabled?: boolean }) {
  const { t, locale } = useI18n();
  const inDesktop = typeof window !== 'undefined' && !!(window as unknown as { oxyDesktop?: unknown }).oxyDesktop;
  const mode: DictationMode = clinicMode === 'browser' && (inDesktop || !SpeechRecognitionCtor()) ? 'device' : clinicMode;
  const [state, setState] = useState<'idle' | 'listening' | 'loading' | 'working' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const recognition = useRef<SpeechRec | null>(null);
  const chunks = useRef<Blob[]>([]);
  const heard = useRef('');
  const stream = useRef<MediaStream | null>(null);
  const errored = useRef(false);

  useEffect(
    () => () => {
      recorder.current?.state === 'recording' && recorder.current.stop();
      recognition.current?.stop();
      stream.current?.getTracks().forEach((tr) => tr.stop());
    },
    [],
  );

  if (mode === 'off') return null;

  const fail = (msg: string) => {
    errored.current = true;
    setState('error');
    setMessage(msg);
    stream.current?.getTracks().forEach((tr) => tr.stop());
  };

  async function startDevice() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return fail(t.dictation.unsupported);
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true } });
    } catch {
      return fail(t.dictation.denied);
    }
    chunks.current = [];
    const rec = new MediaRecorder(stream.current);
    rec.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
    rec.onstop = async () => {
      stream.current?.getTracks().forEach((tr) => tr.stop());
      setState('working');
      setMessage(t.dictation.transcribing);
      try {
        const audio = await toPcm16k(new Blob(chunks.current, { type: rec.mimeType }));
        const w = speechWorker();
        const id = nextId++;
        const text = await new Promise<string>((resolve, reject) => {
          const onMsg = (e: MessageEvent<{ type: string; id?: number; text?: string; message?: string; progress?: number }>) => {
            if (e.data.type === 'progress') {
              setState('loading');
              setMessage(t.dictation.loadingModel);
              setProgress(Math.round(e.data.progress ?? 0));
              return;
            }
            if (e.data.id !== id) return;
            w.removeEventListener('message', onMsg);
            if (e.data.type === 'result') resolve(e.data.text ?? '');
            else reject(new Error(e.data.message));
          };
          w.addEventListener('message', onMsg);
          w.postMessage({ id, audio, language: locale === 'ar' ? 'arabic' : 'english' }, [audio.buffer]);
        });
        setProgress(null);
        if (text) onText(text);
        setState('idle');
        setMessage(null);
      } catch {
        setProgress(null);
        fail(t.dictation.failed);
      }
    };
    recorder.current = rec;
    rec.start();
    setState('listening');
    setMessage(t.dictation.listening);
    // Five minutes at most: a forgotten microphone should not record a whole afternoon.
    window.setTimeout(() => rec.state === 'recording' && rec.stop(), 5 * 60_000);
  }

  function startBrowser() {
    const Ctor = SpeechRecognitionCtor();
    if (!Ctor) return fail(t.dictation.unsupported);
    const rec = new Ctor();
    rec.lang = locale === 'ar' ? 'ar-SA' : 'en-US';
    rec.continuous = true;
    rec.interimResults = false;
    heard.current = '';
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]!;
        if (r.isFinal) heard.current += (heard.current ? ' ' : '') + r[0].transcript.trim();
      }
    };
    rec.onerror = (e) => fail(e.error === 'not-allowed' ? t.dictation.denied : t.dictation.failed);
    rec.onend = () => {
      if (heard.current) onText(heard.current);
      if (!errored.current) {
        setState('idle');
        setMessage(null);
      }
    };
    recognition.current = rec;
    rec.start();
    setState('listening');
    setMessage(t.dictation.listening);
  }

  const toggle = () => {
    if (state === 'listening') {
      if (mode === 'browser') recognition.current?.stop();
      else recorder.current?.stop();
      return;
    }
    setMessage(null);
    errored.current = false;
    if (mode === 'browser') startBrowser();
    else void startDevice();
  };

  const busy = state === 'loading' || state === 'working';
  return (
    <span className="dictate">
      <button
        type="button"
        className={`btn btn-sm ${state === 'listening' ? 'btn-danger dictate-live' : 'btn-ghost'}`}
        onClick={toggle}
        disabled={disabled || busy}
        aria-pressed={state === 'listening'}
        title={mode === 'device' ? t.dictation.device : t.dictation.browser}
      >
        {busy ? <Spinner /> : <IconMic size={15} />}
        {state === 'listening' ? t.dictation.stop : t.dictation.start}
      </button>
      {message ? (
        <span className={`dictate-msg${state === 'error' ? ' is-error' : ''}`} role="status">
          {message}
          {progress !== null && state === 'loading' ? ` ${progress}%` : ''}
        </span>
      ) : null}
    </span>
  );
}
